import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Client-side payment widget — used on the Client Portal Property Detail page.
 * Surfaces:
 *  - Balance & payment mode
 *  - "Charge $X to Visa •••• 4242" 1-click (if saved card)
 *  - "Pay with Stripe" Checkout button
 *  - "Remove saved card" link
 */
export default function ClientPropertyPayment({ appointment, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loading, setLoading] = useState(false);
  const [chargingOnFile, setChargingOnFile] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSummary = useCallback(async () => {
    if (!appointment?.id) return;
    setLoadingSummary(true);
    try {
      const { data } = await axios.get(
        `${API}/api/payments/appointment/${appointment.id}/summary`,
        { headers: authHeader() }
      );
      setSummary(data);
    } catch (e) {
      setSummary({ _error: e?.response?.data?.detail || "Could not load payment info" });
    } finally {
      setLoadingSummary(false);
    }
  }, [appointment?.id]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handlePayNow = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/client-portal/payments/checkout`,
        { appointment_id: appointment.id, origin_url: window.location.origin },
        { headers: authHeader() }
      );
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  const handleChargeOnFile = async () => {
    const card = summary?.saved_method;
    if (!card) return;
    if (!window.confirm(`Charge $${summary.next_due.toFixed(2)} to ${card.brand?.toUpperCase()} •••• ${card.last4}?`)) return;
    setChargingOnFile(true);
    try {
      const { data } = await axios.post(
        `${API}/api/client-portal/payments/charge-on-file`,
        { appointment_id: appointment.id },
        { headers: authHeader() }
      );
      alert(`Charged $${data.amount.toFixed(2)} ✓`);
      await fetchSummary();
      if (onChanged) onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Charge failed";
      if (window.confirm(`${detail}\n\nWould you like to use a different card?`)) {
        handlePayNow();
      }
    } finally {
      setChargingOnFile(false);
    }
  };

  const handleRemoveCard = async () => {
    if (!window.confirm("Remove the saved card from your account?")) return;
    try {
      await axios.delete(`${API}/api/client-portal/payments/saved-method`, { headers: authHeader() });
      await fetchSummary();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to remove card");
    }
  };

  if (loadingSummary) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 animate-pulse text-sm text-gray-400">
        Loading payment…
      </div>
    );
  }

  // If the appointment has no price at all, hide entirely
  if (!summary || (!summary.total_price && !summary._error)) {
    return null;
  }

  // If fully paid, show paid receipt-style block
  const fullyPaid = summary.payment_status === "paid" || (summary.next_due ?? 0) <= 0;
  if (fullyPaid && !summary._error) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-xl p-4"
        data-testid="client-payment-paid"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-semibold text-emerald-900">Paid in full</span>
          <span className="ml-auto text-sm text-emerald-700">${summary.total_price.toFixed(2)}</span>
        </div>
      </div>
    );
  }

  const savedMethod = summary.saved_method;
  const nextDue = summary.next_due || 0;
  const mode = summary.payment_mode || "none";

  return (
    <div className="bg-white border border-indigo-200 rounded-xl p-5 shadow-sm" data-testid="client-payment-widget">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold text-gray-900">Balance Due</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {mode === "partial" && summary.paid_amount > 0
              ? `Deposit paid. Remaining balance.`
              : mode === "partial"
              ? `${Math.round((nextDue / summary.total_price) * 100)}% deposit to confirm booking`
              : mode === "full"
              ? `Full payment due`
              : `Outstanding balance`}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-indigo-700">${nextDue.toFixed(2)}</div>
          {summary.paid_amount > 0 && (
            <div className="text-xs text-gray-400 mt-0.5">
              ${summary.paid_amount.toFixed(2)} paid of ${summary.total_price.toFixed(2)}
            </div>
          )}
        </div>
      </div>

      {summary._error ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">{summary._error}</p>
      ) : (
        <div className="space-y-2">
          {savedMethod && (
            <button
              type="button"
              onClick={handleChargeOnFile}
              disabled={chargingOnFile}
              data-testid="client-charge-on-file-btn"
              className="w-full px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 flex items-center justify-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {chargingOnFile ? "Charging…" : `Charge to ${savedMethod.brand?.toUpperCase()} •••• ${savedMethod.last4}`}
            </button>
          )}
          <button
            type="button"
            onClick={handlePayNow}
            disabled={loading}
            data-testid="client-pay-btn"
            className={`w-full px-4 py-2.5 ${savedMethod ? "bg-white text-indigo-700 border border-indigo-300 hover:bg-indigo-50" : "bg-indigo-600 text-white hover:bg-indigo-700"} text-sm font-medium rounded-lg disabled:opacity-50 transition-colors`}
          >
            {loading ? "Redirecting…" : savedMethod ? "Use a different card" : `Pay $${nextDue.toFixed(2)} with Stripe`}
          </button>
        </div>
      )}

      {savedMethod && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
          <span className="text-gray-500">
            Saved card • {savedMethod.brand?.toUpperCase()} •••• {savedMethod.last4} • exp {savedMethod.exp_month}/{String(savedMethod.exp_year).slice(-2)}
          </span>
          <button onClick={handleRemoveCard} className="text-red-600 hover:underline" data-testid="client-remove-card-btn">
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
